/* ===== pageShell.ts — «غلاف» كل صفحة في المستند الواحد =====
   كان لكل صفحة في الأصل ملف HTML خاص يحمل: <title>، وأوراق أنماطها بترتيبها،
   وسمات <html>/<body>، وعنصر الجذر. في التطبيق ذي الصفحة الواحدة ملف واحد،
   فيُطبَّق غلاف الصفحة عند تركيبها ويُزال غلاف سابقتها — وهذا شرط التطابق البصري:

   1. الأنماط معزولة لكل صفحة. login.css و sign.css فيهما قواعد عامة (body شبكة
      عمودين · form · a · .btn.primary …) لا يجوز أن تصل إلى التطبيق، وترتيب
      التطبيق app ← gantt ← detail ← uat جزء من الشكل (القاعدة المتأخرة تغلب عند
      تساوي الأولوية). لذلك تُستورد الملفات نصّاً خاماً (‎?raw‎ — بايتات الملفات
      المنقولة حرفياً كما قدّمها الأصل، بلا معالجة) وتُركَّب وسومَ <style> في آخر
      <head> بترتيب الأصل، وتُزال كلها عند الانتقال إلى صفحة أخرى. لا يوجد في
      الشيفرة أي `import '…css'` عادي: Vite كان سيحقنه في المستند كله.
   2. <title> كما في ملف الصفحة الأصلي حرفياً.
   3. سمات المستند: <body style="visibility:hidden"> للتطبيق وحده (يُظهره init())؛
      data-theme="light" ثابت لبوابة التوقيع (كان في <html> صفحتها)؛ الدخول
      والتطبيق يضبطان المظهر بنفسيهما كالأصل.
   4. عنصر الجذر بشكل صفحته: display:contents للتطبيق والدخول (#nav/#app، و .side/.pane
      عناصر شبكة body مباشرة)، و <div class="sgn-wrap" id="root"> للبوابة كالأصل. */

import type { PageKind } from './routes';

export interface PageShell {
  kind: PageKind;
  /** نصّ <title> في ملف الصفحة الأصلي. */
  title: string;
  /** أوراق الأنماط بترتيب وسوم الأصل: [اسم للتعرّف, النصّ]. */
  styles: ReadonlyArray<readonly [string, string]>;
  /** <body style="visibility:hidden"> حتى يُظهره الإقلاع (التطبيق وحده). */
  bodyHidden: boolean;
  /** data-theme ثابت في <html> صفحة الأصل (بوابة التوقيع: light). */
  theme?: string;
  /** سمات عنصر الجذر #root. */
  root: { className?: string; style?: string };
}

const STYLE_ATTR = 'data-page-style';

/** يُزيل أنماط الصفحة السابقة ويركّب أنماط هذه بترتيبها، ويضبط العنوان والسمات. */
export function applyPageShell(shell: PageShell, host: HTMLElement): void {
  const head = document.head;
  head.querySelectorAll(`style[${STYLE_ATTR}]`).forEach((el) => el.remove());
  for (const [name, css] of shell.styles) {
    const el = document.createElement('style');
    el.setAttribute(STYLE_ATTR, name);
    el.textContent = css;
    head.appendChild(el);
  }

  document.title = shell.title;
  if (shell.theme) document.documentElement.setAttribute('data-theme', shell.theme);

  if (shell.bodyHidden) document.body.setAttribute('style', 'visibility:hidden');
  else document.body.removeAttribute('style');

  if (shell.root.className) host.className = shell.root.className;
  else host.removeAttribute('class');
  if (shell.root.style) host.setAttribute('style', shell.root.style);
  else host.removeAttribute('style');
}

/** أسماء أوراق الأنماط المركّبة الآن بترتيبها (للاختبارات والفحص). */
export function mountedPageStyles(): string[] {
  return [...document.head.querySelectorAll(`style[${STYLE_ATTR}]`)].map((el) => el.getAttribute(STYLE_ATTR) || '');
}
